import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Loader2, MapPin, Users, FileText, Sun, Moon } from "lucide-react";
import { useTheme } from "@/theme/ThemeProvider";

const FEATURES = [
  { icon: MapPin,    text: "Учёт проживающих по хостелам и компаниям" },
  { icon: FileText,  text: "ИИ распознаёт данные из фото паспорта" },
  { icon: Users,     text: "Отчёты о проживании с экспортом в Excel" },
  { icon: Building2, text: "Уведомления об истечении документов" },
];

export default function HostelLoginPage({ setUser }) {
  const nav = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [tab, setTab]           = useState("login");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } =
        tab === "login"
          ? await authApi.login(email, password)
          : await authApi.hostelRegister(email, password, name);
      localStorage.setItem("tabel_token", data.token);
      localStorage.setItem("tabel_user", JSON.stringify(data.user));
      setUser?.(data.user);
      toast.success(tab === "login" ? "Добро пожаловать" : "Аккаунт создан");
      nav("/hostel/hostels");
    } catch (err) {
      toast.error(extractApiError(err, "Ошибка"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-base)" }}>
      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label="Переключить тему"
        className="fixed top-4 right-4 z-50 p-2 rounded-full"
        style={{
          background: "var(--glass-bg-active)",
          backdropFilter: "blur(16px)",
          border: "1px solid var(--glass-border)",
          color: "var(--foreground-2)",
        }}
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-[52%] relative overflow-hidden flex-col justify-between p-12"
        style={{
          background: "linear-gradient(135deg, #041a14 0%, #062920 50%, #041a14 100%)",
        }}
      >
        <div
          className="absolute pointer-events-none inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 20% 80%, rgba(16,185,129,0.12) 0%, transparent 60%)," +
              "radial-gradient(ellipse 50% 50% at 80% 20%, rgba(59,130,246,0.07) 0%, transparent 55%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative z-10 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
            style={{
              background: "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
              boxShadow: "0 4px 20px rgba(5,150,105,0.4)",
            }}
          >
            Х
          </div>
          <div>
            <div className="text-white font-bold">Про Аутсорс · Хостел</div>
            <div className="text-white/40 text-xs">Модуль управления хостелами</div>
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Учёт проживания{" "}
              <span style={{
                background: "linear-gradient(90deg, #34d399, #22d3ee)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                по паспорту
              </span>
            </h1>
            <p className="mt-4 text-white/50 text-base leading-relaxed max-w-md">
              Загружайте фото паспортов, ИИ извлечёт все данные.
              Ведите учёт хостелов, компаний и выгружайте отчёты.
            </p>
          </div>
          <div className="space-y-3">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: "rgba(16,185,129,0.15)",
                    border: "1px solid rgba(16,185,129,0.25)",
                  }}
                >
                  <Icon className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm text-white/60 pt-1">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-white/20 text-xs">© {new Date().getFullYear()} Про Аутсорс</div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center justify-center gap-2.5 mb-8">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
              style={{
                background: "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                boxShadow: "0 4px 16px rgba(5,150,105,0.35)",
              }}
            >
              Х
            </div>
            <span className="font-bold text-lg" style={{ color: "var(--foreground)" }}>
              Хостел
            </span>
          </div>

          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: "var(--card-bg)",
              backdropFilter: "blur(24px) saturate(180%)",
              WebkitBackdropFilter: "blur(24px) saturate(180%)",
              border: "1px solid var(--card-border)",
              boxShadow: "var(--glass-shadow-lg)",
            }}
          >
            {/* Tabs */}
            <div className="flex" style={{ borderBottom: "1px solid var(--glass-border)" }}>
              {[{ key: "login", label: "Вход" }, { key: "register", label: "Регистрация" }].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  data-testid={`hostel-tab-${key}`}
                  className="flex-1 py-3.5 text-sm font-semibold transition-all"
                  style={{
                    color: tab === key ? "#059669" : "var(--foreground-3)",
                    borderBottom: tab === key ? "2px solid #059669" : "2px solid transparent",
                    background: tab === key ? "rgba(5,150,105,0.08)" : "transparent",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {tab === "register" && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--foreground-3)" }}>
                    Имя
                  </label>
                  <input
                    data-testid="hostel-input-name"
                    placeholder="Иван"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-glass"
                    style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--foreground-3)" }}>Email</label>
                <input
                  type="email"
                  required
                  data-testid="hostel-input-email"
                  placeholder="you@company.ru"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-glass"
                  style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--foreground-3)" }}>Пароль</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  data-testid="hostel-input-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-glass"
                  style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="hostel-submit-btn"
                className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                style={{
                  background: "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                  boxShadow: "0 4px 16px rgba(5,150,105,0.3)",
                }}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {tab === "login" ? "Войти" : "Создать аккаунт"}
              </button>
            </form>
          </div>

          <div className="mt-5 text-center">
            <a href="/login" className="text-sm" style={{ color: "var(--foreground-3)" }}>
              ← Вернуться к основному входу
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
